// src/services/api/referralService.js

// Import the configured apiClient
import { apiClient } from "../../config/api.config";

// Remove the direct axios import and the BASE_URL constant
// import axios from 'axios';
// import { API_BASE_URL } from '../../config/constants';

// Remove the getAuthHeader function as apiClient handles it
// const getAuthHeader = () => {
//   const token = localStorage.getItem('authToken');

//   if (!token) {
//     throw new Error('No auth token found. Please log in.');
//   }
//   return { Authorization: `Bearer ${token}` };
// };

class ReferralService {
  /**
   * Generate a new referral code for the current user
   * @returns {Promise} Promise with the response data
   */
  async generateReferralCode() {
    // Use apiClient instead of axios
    // Remove the manual headers option
    try {
      const response = await apiClient.post(
        "/referrals/generate", // Use relative path
        {} // Include empty object for the body if POST requires one
      );

      return response.data;
    } catch (error) {
      // Rethrow the error for calling code to handle
      throw (
        error.response?.data || new Error("Failed to generate referral code")
      );
    }
  }

  /**
   * Apply a referral code
   * @param {string} referralCode - The referral code to apply
   * @param {string} source - The source of the referral (e.g., 'WEB')
   * @returns {Promise} Promise with the response data
   */
  async applyReferralCode(referralCode, source = "WEB") {
    // Use apiClient instead of axios
    // Remove the manual headers option
    try {
      const response = await apiClient.post(
        "/referrals/apply", // Use relative path
        { referralCode, source } // Send payload directly
      );
      return response.data; // Return the response data
    } catch (error) {
      // Throw the error to be handled by the component
      throw error.response?.data || new Error("Failed to apply referral code");
    }
  }
  //used refferal
  async getUsedReferralCode() {
    try {
      const response = await apiClient.get("/referrals/used-code");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch used referral code")
      );
    }
  }
  /**
   * Get the current user's referral code
   * Note: The original code used POST, which is unusual for fetching data.
   * Refactoring assumes the backend expects a POST with no body for this endpoint.
   * If the backend expects a GET, change apiClient.post to apiClient.get and remove the empty {} body.
   * @returns {Promise} Promise with the response data
   */
  async getReferralCode() {
    // Use apiClient instead of axios
    // Remove the manual headers option
    try {
      const response = await apiClient.post(
        "/referrals/code", // Use relative path
        {} // Assuming backend expects POST with an empty body
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch referral code");
    }
  }

  /**
   * Get the current user's referral statistics
   * @returns {Promise} Promise with the response data
   */
  async getReferralStats() {
    // Use apiClient instead of axios
    // Remove the manual headers option
    try {
      const response = await apiClient.get(
        "/referrals/stats" // Use relative path
        // No need for headers here, apiClient handles it
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || new Error("Failed to fetch referral stats");
    }
  }

  /**
   * Get the current active reward configuration
   * @returns {Promise} Promise with the active config data
   */
  async getActiveConfig() {
    try {
      const response = await apiClient.get("/referrals/active-config");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data ||
        new Error("Failed to fetch active reward configuration")
      );
    }
  }
  async getUsedReferralCode() {
    try {
      const response = await apiClient.get("/referrals/used-code");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch used referral code")
      );
    }
  }
  /**
   * Get the referral history for the current user
   * @returns {Promise} Promise with the response data
   */
  async getReferralHistory() {
    // Use apiClient instead of axios
    // Remove the manual headers option
    try {
      const response = await apiClient.get(
        "/referrals/history" // Use relative path
        // No need for headers here, apiClient handles it
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch referral history")
      );
    }
  }

  /**
   * Process a referral qualification
   * @param {string} referralTrackingId - ID of the referral tracking record
   * @param {object} qualificationData - Data related to qualification
   * @returns {Promise} Promise with the response data
   */
  async processReferralQualification(referralTrackingId, qualificationData) {
    try {
      const response = await apiClient.put(
        `/referrals/qualification/${referralTrackingId}`,
        qualificationData
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data ||
        new Error("Failed to process referral qualification")
      );
    }
  }

  /**
   * Process referral payout with wallet deduction
   * @param {string} referralTrackingId - ID of the referral tracking record
   * @param {object} payoutData - Payout details including amount and admin wallet info
   * @returns {Promise} Promise with the response data
   */
  async processReferralPayout(referralTrackingId, payoutData) {
    try {
      const response = await apiClient.post(
        `/referrals/payout/${referralTrackingId}`,
        {
          ...payoutData,
          deductFromAdminWallet: true, // Flag to indicate admin wallet deduction
        }
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to process referral payout")
      );
    }
  }

  /**
   * Check admin wallet balance
   * @returns {Promise} Promise with admin wallet balance data
   */
  async getAdminWalletBalance() {
    try {
      const response = await apiClient.get("/admin/wallet/balance");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data ||
        new Error("Failed to fetch admin wallet balance")
      );
    }
  }

  /**
   * Get referral payout history
   * @param {object} filters - Optional filters for payout history
   * @returns {Promise} Promise with payout history data
   */
  async getReferralPayoutHistory(filters = {}) {
    try {
      const response = await apiClient.get("/referrals/payout-history", {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data ||
        new Error("Failed to fetch referral payout history")
      );
    }
  }

  /**
   * Get low balance alerts for admin wallet
   * @returns {Promise} Promise with low balance alert data
   */
  async getLowBalanceAlert() {
    try {
      const response = await apiClient.get("/admin/wallet/low-balance-alert");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to fetch low balance alert")
      );
    }
  }

  /**
   * Dismiss low balance alert
   * @returns {Promise} Promise with response data
   */
  async dismissLowBalanceAlert() {
    try {
      const response = await apiClient.post(
        "/admin/wallet/dismiss-low-balance-alert"
      );
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || new Error("Failed to dismiss low balance alert")
      );
    }
  }
}

export default new ReferralService();
